const levels_11_12_13 = [
  {
    id: 11,
    name: 'AI Trading Assistant',
    description: 'Use AI as a second opinion, not a crutch',
    unlock_after: 10,
    cards: [
      {
        id: 1101,
        title: 'What AI Actually Does',
        explanation: 'AI does not predict the future or guarantee outcomes. Instead, it analyzes available data, identifies patterns from history, and presents possible scenarios based on current market conditions. AI processes information faster than humans but within the same analytical framework. It finds correlations that humans might miss.',
        rule: 'AI gives probability-based insights, not certainty.',
        visual: 'ai_probability_insights',
        context: 'Nyx analyzes a stock and says:\n\nprice near resistance\nRSI at 65\nvolume increasing\n\nIt suggests: 👉 "Possible breakout setup forming"\n\nThis does NOT mean: 👉 breakout will definitely happen\n\nIt means: 👉 conditions are aligning based on history\n\n👉 Probability increased, not certainty\n\n👉 Final outcome still depends on market behavior',
      },
      {
        id: 1102,
        title: 'AI Is Not a Signal Provider',
        explanation: 'AI suggestions are not direct buy/sell signals. They are structured insights and probabilities that require your validation. AI output must be checked against your chart, patterns, and understanding. AI removes guesswork but does not remove the need for verification. You remain the decision maker.',
        rule: 'AI suggests. You decide.',
        visual: 'ai_suggestion_validation',
        context: 'Nyx suggests:\n\n"Bullish setup  -  entry near ₹250, SL ₹240, target ₹280"\n\nYou blindly follow without chart check\n\nBut on chart you see:\n\nresistance just above ₹255\nweak volume\ncandle closing poorly\n\nTrade fails\n\n👉 AI gave a structured idea\n👉 You skipped validation\n\n👉 This was your mistake, not AI\'s',
      },
      {
        id: 1103,
        title: 'How AI Builds a Trade Idea',
        explanation: 'AI combines multiple factors systematically  -  price action, trend direction, technical indicators, volume patterns, and historical setup patterns  -  to generate a trade idea. AI connects individual signals into a structured view showing how they reinforce each other. This is confluence building at scale.',
        rule: 'AI connects signals into a structured view.',
        visual: 'ai_trade_idea_flow',
        context: 'Nyx analyzes a stock and generates:\n\nTrend: price above SMA50 (bullish)\nMomentum: MACD bullish crossover (momentum shifting)\nStructure: consolidation breakout forming (key resistance)\nVolume: increasing significantly (participation growing)\n\nBased on this confluence, it suggests:\n\n👉 "High-probability bullish setup"\n\nThis is not random opinion\n👉 it\'s 4 independent conditions aligning',
      },
      {
        id: 1104,
        title: 'AI + Your Understanding = Edge',
        explanation: 'AI becomes powerful only when combined with your understanding of charts, price patterns, and market behavior. AI handles data processing; you handle judgment. A knowledgeable trader using AI becomes exponentially better. An uninformed trader using AI becomes dependent. The edge comes from the combination, not from AI alone.',
        rule: 'AI without understanding = dependency\nAI with understanding = advantage',
        visual: 'ai_edge_validation',
        context: 'Nyx suggests a breakout trade\n\nTrader A (Blind user):\n👉 enters immediately without chart check\n\nTrader B (Smart user):\nOK checks resistance clearly visible\nOK verifies volume increasing\nOK confirms clean structure\n👉 then enters with conviction\n\nSame AI suggestion\nDifferent execution\nDifferent outcomes',
      },
      {
        id: 1105,
        title: 'When AI Is Wrong',
        explanation: 'AI can be wrong because market conditions change unpredictably and suddenly. AI works on historical patterns and current data. When external factors enter (earnings, geopolitical events, news), conditions shift instantly. AI was correct based on data available at analysis time. But markets are probabilistic, not deterministic.',
        rule: 'No system  -  human or AI  -  is always right.',
        visual: 'ai_wrong_prediction',
        context: 'Nyx suggests bullish setup with high probability\n\nSetup was valid 30 minutes ago\n\nSuddenly:\n\nnews hits market\nheavy institutional selling starts\n\nPrice drops sharply\n\n👉 AI analysis was correct (based on available data)\n👉 Market changed suddenly\n\n👉 This is not AI failure',
      },
      {
        id: 1106,
        title: 'AI Does Not See Everything',
        explanation: 'AI works only on data available in the system. It analyzes price, volume, technicals, and news sentiment. But it cannot always account for sudden events, management changes, or external factors that emerge without warning. AI is limited to knowable information. Unknown events are by definition outside its reach.',
        rule: 'AI has limitations  -  don\'t treat it as complete intelligence.',
        visual: 'l11_c1106_ai_limitations',
        context: 'Nyx analyzes a stock and suggests:\n\nstrong uptrend confirmed\ngood momentum building\nhigh probability setup\n\nBut then:\n\ncompany results released\nunexpected negative surprise\nstock crashes\n\n👉 AI didn\'t fail (analysis was correct)\n👉 Data changed suddenly and unpredictably\n\n👉 AI needs new data to adapt',
      },
      {
        id: 1107,
        title: 'AI as a Second Opinion',
        explanation: 'The best use of AI is as a second opinion  -  to validate or challenge your thinking. You form an idea, AI provides independent analysis of the same setup. If AI agrees, conviction increases. If AI disagrees, you reconsider. AI becomes a thinking partner, not a decision maker.',
        rule: 'Use AI to question your decision, not replace it.',
        visual: 'l11_c1107_ai_second_opinion',
        context: 'You see a potential setup:\n\nprice near level\nwant to buy\n\nYou ask Nyx for analysis\n\nNyx says: 👉 "Weak volume, near resistance, risky entry timing"\n\nNow you pause and reconsider\n\n👉 You avoid a bad trade\n\n👉 AI prevented losses through disagreement',
      },
      {
        id: 1108,
        title: 'AI and Overconfidence Trap',
        explanation: 'Users often become overconfident when AI supports their idea. Confirmation bias kicks in - when AI agrees with you, you feel validated. But agreement from AI does not increase actual probability of success. It only increases your confidence level. Confidence and probability are not the same thing.',
        rule: 'Agreement from AI does not increase certainty.',
        visual: 'l11_c1108_ai_overconfidence_trap',
        context: 'You think:\n\n👉 "This stock will go up"\n\nNyx analysis also shows bullish setup\n\nYou feel validated\n\nYou double your position size\n\nTrade fails\n\n👉 Mistake was not AI\n👉 Mistake was overconfidence from agreement\n\n👉 Same probability, higher position size',
      },
      {
        id: 1109,
        title: 'AI Trade Quality Feedback',
        explanation: 'AI evaluates your trade decisions based on structure, timing, risk-reward ratio, and confluence. After you execute a trade, AI provides feedback on decision quality, not just outcome. This helps you improve your process independent of whether the trade won or lost.',
        rule: 'AI helps you improve behavior, not just trades.',
        visual: 'l11_c1109_ai_trade_feedback',
        context: 'You take a trade:\n\n👉 late entry (move already happened)\n👉 poor risk-reward (risk 8, reward 5)\n👉 weak confluence\n\nNyx feedback:\n\n👉 "Entry was late, risk high vs reward, confluence weak"\n\nYou review and improve next trade\n\n👉 Better process over time',
      },
      {
        id: 1110,
        title: 'Asking AI the Right Way',
        explanation: 'The quality of AI response depends directly on how you ask. Vague questions produce vague answers. Specific questions with context produce detailed analysis. Give AI the chart setup, your conditions, and your concerns. The more context you provide, the better the feedback.',
        rule: 'Specific questions give better answers.',
        visual: 'l11_c1110_ai_questioning',
        context: 'Bad question:\n👉 "What should I buy?"\n\n👉 AI cannot answer this\n\nGood question:\n👉 "Stock near ₹250 resistance, volume rising 2x, RSI 60, SMA20 crossed above SMA50  -  breakout likely or false breakout?"\n\nNow AI gives:\n\nOK structured answer with analysis\nOK contextual evaluation\nOK risk factors',
      },
      {
        id: 1111,
        title: 'AI Is a Tool, Not a Strategy',
        explanation: 'AI supports your strategy - it does not replace it. Without your rules and structure, AI suggestions become noise. With a clear strategy, AI becomes a powerful validation and execution tool. AI fills gaps in your analysis but cannot create direction from nothing.',
        rule: 'Without your rules, AI becomes noise.',
        visual: 'l11_c1111_ai_as_tool',
        context: 'Trader without strategy:\n\nNo rules\nOpens Nyx daily\nTakes random suggestions\nchases signals\n\n👉 inconsistent results\n\nTrader with strategy:\n\nOK breakout strategy defined\nOK trend filter (only uptrends)\nOK uses AI only for validation\nOK ignores AI suggestions outside strategy\n\n👉 consistent, disciplined execution',
      },
      {
        id: 1112,
        title: 'Correct Use of AI (Complete Flow)',
        explanation: 'AI should be used within a structured decision flow, not as a shortcut around thinking. AI works best when integrated into your process at specific points. This creates a hybrid system: human judgment + AI analysis + structured execution.',
        rule: 'AI fits inside your process  -  not outside it.',
        visual: 'l11_c1112_ai_correct_use',
        context: 'IDEAL FLOW:\n\n1. You identify setup (from chart/scanner)\n2. You form your trading idea\n3. You check with AI\n4. AI highlights strengths/risks/confluence\n5. You finalize decision with full context\n6. You execute with predefined structure\n\n👉 AI becomes part of your thinking process\n\nWrong flow:\n\nAI -> suggestion -> direct trade -> regret\n\n👉 No thinking -> no learning -> no improvement',
      },
    ],
    checkpoints: [],
  },
  {
    id: 12,
    name: 'Validate Before You Trade',
    description: 'Test your ideas before risking real capital',
    unlock_after: 11,
    cards: [
      {
        id: 1201,
        title: 'What Backtesting Actually Means',
        explanation: 'Backtesting means applying your trading idea to past market data to see how it would have performed. You define a rule, apply it to historical data, and measure outcomes. This converts your opinion into testable fact. Backtesting is not prediction - it is historical validation.',
        rule: 'Backtesting answers one question:\n👉 "Did this idea actually work before?"',
        visual: 'l12_c1201_what_backtest',
        context: 'You believe:\n\n👉 "Buying when RSI is oversold works"\n\nInstead of guessing, you backtest:\n\nlast 6 months of data\nevery time RSI dropped below 30\ncheck what happened in next candles\n\nNow you see:\n\n15 trades: 6 worked, 9 failed\nwin rate: 40%\n\n👉 Opinion becomes data',
      },
      {
        id: 1202,
        title: 'Idea vs Reality',
        explanation: 'Many trading ideas sound good logically but fail when tested on real data. An idea that makes sense in theory often breaks against market reality. Backtesting closes the gap between what feels true and what actually happened.',
        rule: 'If it\'s not tested, it\'s just an opinion.',
        visual: 'l12_c1202_idea_vs_reality',
        context: 'You think:\n\n👉 "Buying breakouts always leads to profit"\n\nYour logic: resistance broken = buyers winning\n\nYou backtest:\n\n20 breakout trades over 3 months\n\nResults:\n\n11 failed (false breakouts)\n9 worked (true breakouts)\n\n👉 Idea felt strong\n👉 Reality = 45% win rate',
      },
      {
        id: 1203,
        title: 'One Example Is Not Enough',
        explanation: 'A single successful trade proves nothing about your strategy. One win could be luck. You need multiple tests - at least 30 trades - to understand true consistency. Consistency reveals whether you have an edge or just got lucky.',
        rule: 'Consistency matters more than isolated success.',
        visual: 'l12_c1203_one_example',
        context: 'You see one perfect trade:\n\nbreakout setup\nbig profit\n\nYou believe:\n👉 "My strategy works!"\n\nBut after backtesting 30 similar trades:\n\nonly 12 profitable\nothers lost\n\n👉 One example gave false confidence\n👉 True win rate = 40%, not 100%\n\n👉 Luck vs edge',
      },
      {
        id: 1204,
        title: 'What Backtesting Actually Shows',
        explanation: 'Backtesting shows patterns in past performance. It reveals probability patterns but not exact future results. Your past 40% win rate suggests ~40% future probability, not certainty. Backtesting measures consistency and helps you understand your edge.',
        rule: 'It gives probability, not prediction.',
        visual: 'l12_c1204_backtest_shows',
        context: 'Your strategy backtest shows:\n\n40% win rate\nbut average winning trade = ₹500\naverage losing trade = ₹250\n\nThis means:\n\nOK you can be profitable\nOK even with more losses than wins\n\n👉 Because wins pay 2x more than losses\n\n👉 Past data suggests future probability',
      },
      {
        id: 1205,
        title: 'Risk-Reward Becomes Visible',
        explanation: 'Backtesting reveals your risk-reward ratio. This is critical: a strategy with poor risk-reward (risk 5 to make 3) will bleed capital even with high win rates. A strategy with great risk-reward (risk 2 to make 6) can be profitable with lower win rates.',
        rule: 'A strategy with good risk-reward can survive losses.',
        visual: 'l12_c1205_rr_visible',
        context: 'Strategy A:\n\nwin ₹100 per trade\nlose ₹100 per trade\n50% win rate\n-> break-even\n\nStrategy B:\n\nwin ₹300 per trade\nlose ₹100 per trade\n35% win rate\n-> profitable\n\n👉 Strategy B wins less but pays more\n👉 Better risk-reward matters more',
      },
      {
        id: 1206,
        title: 'When a Strategy Looks Good but Isn\'t',
        explanation: 'Some strategies look profitable in a short period but fail over longer data. A strategy might work for 5 days in a trending market but break during sideways consolidation. Testing requires sufficient data to cover different market conditions.',
        rule: 'Short-term success can be misleading.',
        visual: 'l12_c1206_looks_good_isnt',
        context: 'You backtest 5 days:\n\n👉 5 trades, all profit\n👉 strategy looks amazing\n\nYou backtest 3 months:\n\n👉 2 weeks trending up (strategy worked)\n👉 2 weeks sideways (strategy failed)\n👉 1 week trending down (strategy failed)\n\n👉 Overall = losses\n\n👉 Limited data gave false confidence',
      },
      {
        id: 1207,
        title: 'Market Conditions Change',
        explanation: 'A strategy may work brilliantly in trending markets but fail completely in sideways consolidation. No strategy works in all conditions. Good backtesting reveals not just whether strategy works, but under what conditions it works or breaks.',
        rule: 'No strategy works in all conditions.',
        visual: 'l12_c1207_conditions_change',
        context: 'Breakout strategy backtest results:\n\nOK Trending markets: 65% win rate, profitable\nX Sideways markets: 30% win rate, losses\nX Reversing markets: 25% win rate, significant losses\n\n👉 Strategy is conditional\n👉 You must only use it in trending environments\n\n👉 Now you understand WHERE to apply it',
      },
      {
        id: 1208,
        title: 'Overfitting Trap',
        explanation: 'Overfitting means designing a strategy that works perfectly on past data but fails in real trading. You tweak rules to eliminate historical losses. But tweaking for past data creates a system that does not generalize to new conditions.',
        rule: 'Perfect past performance can be dangerous.',
        visual: 'l12_c1208_overfitting',
        context: 'You backtest and tweak:\n\nadjust entry rules\nfilter out bad trades\nadapt to past losses\n\nBacktest results: 95% win rate (perfect!)\n\nBut in real market:\n\n👉 new conditions emerge\n👉 your tweaked rules don\'t work\n👉 strategy fails\n\n👉 You optimized for past, not reality',
      },
      {
        id: 1209,
        title: 'Backtesting Without Discipline Is Useless',
        explanation: 'If you do not follow the same rules consistently during backtesting, results become worthless. Skipping bad trades or including good trades selectively makes backtest biased. You must apply rules mechanically - no exceptions, no judgment calls, no cherry-picking.',
        rule: 'Consistency in testing = reliable results.',
        visual: 'l12_c1209_discipline_required',
        context: 'Undisciplined backtest:\n\nsometimes you skip bad trades\nsometimes you include good trades\nsometimes you adjust the rule\n\n👉 Results become completely biased\n\nYou are not testing a strategy\n👉 You are selecting outcomes\n\nDisciplined backtest:\n\nApply rules mechanically\nNo exceptions\nNo skipping trades\n👉 Results are reliable',
      },
      {
        id: 1210,
        title: 'What Backtesting Cannot Tell You',
        explanation: 'Backtesting cannot predict exact future behavior or account for sudden events. A strategy that worked 80% of the time in 2023 might work 40% in 2024 if conditions shift. Backtesting is a confidence builder, not a certainty creator.',
        rule: 'Backtesting reduces uncertainty  -  it does not remove it.',
        visual: 'l12_c1210_cannot_tell',
        context: 'Your strategy backtest shows:\n\nOK 65% win rate over past 6 months\nOK consistently profitable\n\nBut in real future trading:\n\nnews event hits market\ngeopolitical crisis emerges\nmarket structure shifts\n\n👉 Your results differ from backtest\n\n👉 Backtesting is confidence, not certainty',
      },
      {
        id: 1211,
        title: 'From Testing to Confidence',
        explanation: 'Backtesting builds confidence in your strategy - not because it wins always, but because you understand its behavior completely. You know its win rate, average loss, drawdown, conditions. This knowledge lets you hold through losses without panic.',
        rule: 'Confidence comes from knowing how your strategy performs.',
        visual: 'l12_c1211_testing_to_confidence',
        context: 'After thorough backtesting, you know:\n\nOK You lose 6 out of 10 trades\nOK Average loss = ₹200\nOK Average win = ₹500\nOK Maximum drawdown = 15%\nOK It works best in uptrends\n\nNow in real trading:\n\nYou take losses without panic\n👉 because you expected them\n👉 you know they\'re part of the system',
      },
      {
        id: 1212,
        title: 'Correct Use of Backtesting',
        explanation: 'Backtesting should be part of a structured workflow. Test first. Understand completely. Then apply. Backtesting without action is procrastination. Action without backtesting is guessing. The power comes from the sequence.',
        rule: 'Test -> Understand -> Apply -> Review',
        visual: 'l12_c1212_correct_use',
        context: 'IDEAL FLOW:\n\n1. You form an idea\n👉 "Buy on breakout + volume spike"\n\n2. You backtest\n👉 check 3+ months of data\n\n3. You analyze thoroughly\n👉 win rate 45%, avg win ₹500, avg loss ₹250\n\n4. You apply in practice mode\n👉 manually trade using your rules\n\n5. You review outcomes\n👉 compare live vs backtest results\n\nWrong flow:\n\nIdea -> trade immediately -> regret\n\n👉 no validation -> random results',
      },
    ],
    checkpoints: [],
  },
  {
    id: 13,
    name: 'Automate Your Edge',
    description: 'Automate your edge with discipline',
    unlock_after: 12,
    cards: [
      {
        id: 1301,
        title: 'What Algo Trading Really Is',
        explanation: 'Algo trading means using a system to execute trades automatically based on predefined rules. You define the logic. Algo executes it without emotion, hesitation, or deviation. Algo is a discipline machine - it enforces rules you might break manually.',
        rule: 'Algo trading executes your logic  -  it does not create logic.',
        visual: 'l13_c1301_algo_what_is',
        context: 'You define exact rules:\n\nOK Buy when price breaks resistance\nOK Only if volume is 2x average\nOK Stop loss at support level\nOK Target 2x your risk\nOK Max 3 trades per day\n\nAlgo does exactly this:\n\nmonitors market 24/7\nexecutes instantly when all conditions match\nstops when daily limit reached\n\n👉 It is not thinking or judging\n👉 It is mechanically following',
      },
      {
        id: 1302,
        title: 'Automation Without Edge Is Dangerous',
        explanation: 'If your strategy is not tested and proven, automating it will only increase losses. An untested idea automated becomes a capital-destroying machine. Automation amplifies both wins and losses. Without an edge, you just lose faster.',
        rule: 'Bad strategy + automation = faster failure',
        visual: 'l13_c1302_no_edge_dangerous',
        context: 'You believe:\n\n👉 "Buy when RSI oversold"\n\nNo backtesting\nYou automate immediately\n\nSystem runs for a week:\n\nX 7 losses (avg ₹500)\nOK 3 wins (avg ₹200)\n\n👉 Losses accumulate fast\n👉 Capital reduces ₹3000 in one week\n\nWhy? Idea never validated\n👉 Automation just accelerates failure',
      },
      {
        id: 1303,
        title: 'From Idea to Automation',
        explanation: 'A strategy must go through multiple validation stages before automation. Idea -> Backtest -> Manual Practice -> Automation. Skip any stage and risk increases dramatically. This sequence builds confidence and reveals weaknesses before automation amplifies them.',
        rule: 'No testing -> no automation',
        visual: 'l13_c1303_idea_to_auto',
        context: 'CORRECT FLOW:\n\n1. Idea:\n👉 "Breakout + volume spike works"\n\n2. Backtest:\n👉 test on 3+ months historical data\n👉 results: 55% win rate, profitable\n\n3. Manual Practice:\n👉 manually trade using rules for 1 month\n👉 confirm results match backtest\n\n4. Automate:\n👉 now automate with confidence\n\n👉 Skipping steps -> rapid failure',
      },
      {
        id: 1304,
        title: 'Algo Removes Emotion, Not Risk',
        explanation: 'Algo eliminates emotional interference - fear, hope, greed. Algo executes mechanically. But this does not remove market risk. A great strategy can still lose. Algo removes bad behavior, not market uncertainty.',
        rule: 'Automation does not guarantee profit.',
        visual: 'l13_c1304_emotion_not_risk',
        context: 'Manual trader:\n\nX exits early due to fear\nX holds due to hope\nX moves SL due to desperation\n\nAlgo:\n\nOK exits exactly at SL\nOK books profit at target\nOK never moves stops\n\nBut:\n\nIf market conditions shift\n\n👉 losses still happen\n👉 No system avoids losses',
      },
      {
        id: 1305,
        title: 'Speed Advantage of Algo',
        explanation: 'Algo reacts faster than humans. Algo detects conditions and executes in milliseconds. Humans need seconds or minutes. This speed advantage is real - better entry price, faster stop loss execution. But speed amplifies both wins and losses equally.',
        rule: 'Speed improves execution, not decision quality.',
        visual: 'l13_c1305_speed_advantage',
        context: 'Breakout happens at ₹500\n\nManual trader:\n\nsees after few seconds\nenters at ₹505\n\nAlgo:\n\ndetects instantly\nenters at ₹500\n\n👉 Better entry price\n\nBut if breakout fails and reverses:\n\nOK Algo exits at SL faster\nOK Saves ₹5 per share\n\n👉 Speed helps in both directions',
      },
      {
        id: 1306,
        title: 'Overtrading Risk in Algo',
        explanation: 'Without limits, algo can take too many trades in a short time. A sideways market triggers repeated false signals. Each small loss accumulates. Overtrading destroys what a good strategy built. Limits must control frequency.',
        rule: 'More trades != better performance',
        visual: 'l13_c1306_overtrading_risk',
        context: 'Algo runs without restrictions\n\nMarket consolidates (sideways)\n\nAlgo keeps triggering signals:\n\ntrade 1: buy -> loss ₹200\ntrade 2: sell -> loss ₹150\ntrade 3: buy -> loss ₹250\n\n10 trades in one day\n\n👉 Small losses accumulate\n👉 Day ends with ₹2000 loss\n\n👉 Overtrading destroys capital',
      },
      {
        id: 1307,
        title: 'Importance of Trade Limits & Cooldown',
        explanation: 'You must control how frequently the algo trades. Limits prevent overtrading. Cooldown prevents repeated signals on same stock in bad conditions. These controls are not restrictions - they are risk management.',
        rule: 'Control frequency to control risk.',
        visual: 'l13_c1307_limits_cooldown',
        context: 'You set intelligent limits:\n\nOK max 3 trades per day\nOK 30-minute cooldown per stock\nOK max 1% loss per trade\n\nNow behavior:\n\nAfter first loss -> cooldown starts\nOK system pauses this stock\nOK prevents repeated bad trades\n\nWithout these limits:\n\nX system keeps firing in bad conditions\nX losses pile up\n\n👉 Limits protect capital',
      },
      {
        id: 1308,
        title: 'Same Strategy, Different Results',
        explanation: 'Even automated strategies do not give identical results every day. Market conditions change. An uptrend day looks different from a sideways day. Same strategy in different environments produces different results. This is normal, not failure.',
        rule: 'Consistency comes from process, not daily outcome.',
        visual: 'l13_c1308_same_diff_results',
        context: 'Day 1 (Trending):\n\n3 trades -> 3 profits\n-> ₹1500 net\n\nDay 2 (Sideways):\n\n3 trades -> 3 losses\n-> ₹1000 loss\n\nStrategy is identical\nMarket conditions completely different\n\n👉 This is expected\n\n👉 Over time, uptrend days offset sideways days',
      },
      {
        id: 1309,
        title: 'Monitoring Is Still Required',
        explanation: 'Algo is not "set and forget." It needs monitoring and human oversight. Market conditions shift suddenly. Your strategy might be wrong for current conditions. Monitoring lets you pause or adjust. You remain the ultimate decision maker.',
        rule: 'You control the system  -  not the other way.',
        visual: 'l13_c1309_monitoring_required',
        context: 'Algo running (was profitable)\n\nMarket suddenly becomes volatile\n\nYou monitor and observe:\n\n5 trades in 30 min\nall SL hits\nlosses accumulating\n\nYou decide:\n👉 "Conditions not right, pausing strategy"\n\nYou pause manually\n\n👉 This is correct control\n\n👉 You remain in charge',
      },
      {
        id: 1310,
        title: 'When NOT to Run Algo',
        explanation: 'Certain conditions are not suitable for automation. When market has no trend, volume is low, and price moves randomly, automation backfires. Algo triggers on noise. Better to pause until conditions improve.',
        rule: 'Avoid running algo in unclear markets.',
        visual: 'l13_c1310_when_not_to_run',
        context: 'Market conditions:\n\nX no visible trend\nX price consolidating sideways\nX volume drying up\nX random movement\n\nAlgo keeps triggering signals\n\n👉 Results = repeated small losses\n👉 Capital bleeds daily\n\nBetter decision:\n\n👉 Pause system until trend emerges\n👉 Wait for clear conditions',
      },
      {
        id: 1311,
        title: 'Discipline Through Automation',
        explanation: 'Algo enforces discipline by removing emotional interference. When you feel like breaking rules, algo cannot be talked into it. Stops are stops. Targets are targets. This mechanical enforcement is algo\'s superpower.',
        rule: 'Algo follows rules even when you don\'t feel like it.',
        visual: 'l13_c1311_discipline_auto',
        context: 'Manual trader:\n\nSL triggered at ₹100\nYou think: "it might bounce"\nYou move SL to ₹95\nPrice crashes to ₹80\nloss ₹20 instead of ₹5\n\nAlgo:\n\nSL hit at ₹100 -> exit immediately\n\n👉 Loss = exactly ₹5\n👉 discipline enforced\n\n👉 No emotional negotiation',
      },
      {
        id: 1312,
        title: 'Complete System Flow',
        explanation: 'Algo trading is the final stage of a structured process - not the starting point. The full journey goes: learn -> practice -> validate -> then automate. Each stage builds on the previous. Skip stages and the system fails.',
        rule: 'Learn -> Test -> Practice -> Automate',
        visual: 'l13_c1312_system_flow',
        context: 'COMPLETE SYSTEM FLOW:\n\n1. Learn foundations (Levels 1-9)\n👉 understand trading principles\n\n2. Practice decisions (Level 10)\n👉 manually execute rules\n\n3. Use AI analysis (Level 11)\n👉 validate your ideas\n\n4. Backtest strategy (Level 12)\n👉 prove edge historically\n\n5. Automate with algo\n👉 enforce discipline\n\n👉 This creates a complete system\n\nWrong flow:\nAlgo first -> no foundation -> guaranteed failure',
      },
    ],
    checkpoints: [],
  },
  {
    id: 70,
    name: 'Scanner Signals (Reference)',
    description: 'Your daily tool. Know what every signal means.',
    unlock_after: null,
    cards: [
      // MOMENTUM CATEGORY (7 cards)
      {
        id: 7001,
        title: 'RSI Oversold',
        explanation: 'RSI dropped below 30  -  selling has been overdone. At this level buyers often step in for a bounce or reversal.',
        rule: 'BUY  -  wait for a bullish reversal candle near support to confirm. SL below the recent swing low.',
        visual: 'RSI_OVERSOLD',
        context: 'Level 2 myth: "Oversold = buy immediately." Now you know: it\'s a signal, needs reversal candle confirmation.',
      },
      {
        id: 702,
        title: 'RSI Overbought',
        explanation: 'RSI rose above 70  -  buying has been excessive. Stock may pause or reverse down from here.',
        rule: 'AVOID new longs. For shorts, wait for RSI to roll over. SL above recent high.',
        visual: 'RSI_OVERBOUGHT',
        context: 'Combined with resistance rejection (Level 6, Pattern 11) = high-probability short signal.',
      },
      {
        id: 703,
        title: 'RSI Cross Above 60',
        explanation: 'RSI crossed above 60  -  momentum is building upward. Still below overbought zone, so trend entry is valid.',
        rule: 'BUY on shallow pullback. Confirm price is above SMA20. SL below SMA20.',
        visual: 'RSI_CROSS_ABOVE_60',
        context: '"Goldilocks zone"  -  strong enough for momentum, not stretched enough for overbought.',
      },
      {
        id: 704,
        title: 'RSI Cross Below 40',
        explanation: 'RSI dropped below 40  -  upward momentum is weakening. Not yet oversold but shows sellers are taking control.',
        rule: 'AVOID new longs. Watch for SMA20 to break as confirmation of further decline.',
        visual: 'RSI_CROSS_BELOW_40',
        context: 'Early warning  -  not a sell signal yet. Need bigger picture: is breakdown following or just pullback?',
      },
      {
        id: 705,
        title: 'MACD Bullish Crossover',
        explanation: 'MACD line just crossed above the signal line  -  bullish momentum is starting. One of the most reliable trend-change signals.',
        rule: 'BUY  -  enter on a pullback to SMA20. Works best when price is already above SMA50.',
        visual: 'MACD_BULLISH_CROSSOVER',
        context: 'Most-used condition in Algo Flows. "MACD Bullish Crossover + Price Above SMA50" = core logic.',
      },
      {
        id: 706,
        title: 'MACD Bearish Crossover',
        explanation: 'MACD line crossed below the signal line  -  bearish momentum is starting. Trend is turning down.',
        rule: 'SELL  -  exit existing longs. Short on a failed bounce. SL above the crossover candle high.',
        visual: 'MACD_BEARISH_CROSSOVER',
        context: 'Combined with Death Cross = very bearish double confirmation.',
      },
      {
        id: 707,
        title: 'MACD Histogram Positive',
        explanation: 'MACD histogram is positive  -  bulls are currently in control but no crossover yet. Use as a bullish filter.',
        rule: 'Use as a BUY filter alongside other signals. Best combined with RSI > 50 and price above SMA20.',
        visual: 'MACD_HISTOGRAM_POSITIVE',
        context: '"Trend is your friend" filter. Not an entry alone  -  tells you the wind is at your back for longs.',
      },

      // TREND CATEGORY (7 cards)
      {
        id: 708,
        title: 'Price Above SMA20',
        explanation: 'Price is above its 20-day moving average  -  short-term trend is up. SMA20 acts as dynamic support.',
        rule: 'Bullish bias. Enter on dips toward SMA20. SL on a close below SMA20.',
        visual: 'PRICE_ABOVE_SMA20',
        context: 'Most common Algo Flows condition  -  simplest way to confirm short-term trend.',
      },
      {
        id: 709,
        title: 'Price Above SMA50',
        explanation: 'Price is above its 50-day moving average  -  medium-term trend is bullish. SMA50 is a key institutional support level.',
        rule: 'Medium-term bullish context. Prefer entries near SMA50 for better risk. Avoid chasing.',
        visual: 'PRICE_ABOVE_SMA50',
        context: 'UPTREND/DOWNTREND filter compares price to SMA50. "Uptrend only" strategy makes this exact check.',
      },
      {
        id: 710,
        title: 'Price Above SMA200',
        explanation: 'Price is above its 200-day moving average  -  long-term uptrend confirmed. Stock is in a structural bull market.',
        rule: 'Long-term bullish context. Use pullbacks to SMA50/SMA20 for entries. Do not short this stock.',
        visual: 'PRICE_ABOVE_SMA200',
        context: 'Above SMA200 = "investable territory." Edge Report uses this as key factor for BUY verdict.',
      },
      {
        id: 711,
        title: 'Price Below SMA20',
        explanation: 'Price dropped below its 20-day average  -  short-term trend is bearish. SMA20 now acts as resistance.',
        rule: 'SELL  -  avoid longs. Watch for failed retests of SMA20 as short entry. SL above SMA20.',
        visual: 'PRICE_BELOW_SMA20',
        context: 'Warning flag for any open long. Short-term trend shifted against you  -  review SL.',
      },
      {
        id: 712,
        title: 'Golden Cross',
        explanation: 'SMA50 crossed above SMA200  -  the single most bullish long-term signal. Marks transition from bear to bull market.',
        rule: 'Major BUY signal. Buy on significant pullbacks. Long-term holds work well after golden cross.',
        visual: 'GOLDEN_CROSS',
        context: 'Happens 1-2x per year per stock. Structural shift  -  Nyx generates multi-week swing suggestions.',
      },
      {
        id: 713,
        title: 'Death Cross',
        explanation: 'SMA50 crossed below SMA200  -  major bearish signal. Marks the beginning of a longer-term downtrend.',
        rule: 'SELL  -  avoid new longs, exit existing. Only trade SELL setups until SMA50 recovers above SMA200.',
        visual: 'DEATH_CROSS',
        context: 'Opposite of Golden Cross. Algo Flows with "uptrend only" stop entering when this fires.',
      },
      {
        id: 714,
        title: 'ADX Strong Trend',
        explanation: 'ADX above 25  -  there is a strong trend in place. ADX is direction-neutral: check price vs SMA50 to know which way.',
        rule: 'Price above SMA50 -> BUY setups. Price below SMA50 -> SELL setups. Never trade against a strong ADX trend.',
        visual: 'ADX_STRONG_TREND',
        context: 'Level 4 concept "Strength vs Weakness" is measured by ADX. High ADX = strong moves, trend strats work.',
      },

      // PRICE ACTION CATEGORY (5 cards)
      {
        id: 715,
        title: 'Near 52-Week High',
        explanation: 'Within 5% of the 52-week high. Stock is showing relative strength  -  may be setting up for a new high breakout.',
        rule: 'BUY on breakout above the 52W high with volume confirmation. SL below the recent consolidation.',
        visual: 'NEAR_52W_HIGH',
        context: 'Strongest stocks in market. Nyx Trader Mode picks these for intraday  -  momentum plays near highs.',
      },
      {
        id: 716,
        title: 'Near 52-Week Low',
        explanation: 'Within 5% of the 52-week low  -  potential bottoming zone, but could be a falling knife. Requires confirmation.',
        rule: 'Speculative BUY only after a confirmed reversal candle at support. Use small size. SL below 52W low.',
        visual: 'NEAR_52W_LOW',
        context: 'Level 2 myth: "Low price != cheap." These get AVOID/WAIT verdict unless signals align.',
      },
      {
        id: 717,
        title: '52-Week High Today',
        explanation: 'Stock made a new 52-week high today  -  momentum breakout. Stocks breaking to new highs often continue higher.',
        rule: 'BUY on breakout day or next day pullback. Momentum plays work best. SL below previous day\'s low.',
        visual: 'breakout',
        context: 'Freshest signal possible. With BREAKOUT_VOLUME_SPIKE = highest-conviction setup.',
      },
      {
        id: 718,
        title: 'Close Above Prev High',
        explanation: 'Today\'s close is above yesterday\'s high  -  buyers overpowered yesterday\'s sellers. Clear sign of upward momentum.',
        rule: 'BUY  -  enter on next open or pullback to yesterday\'s high (now support). SL below today\'s low.',
        visual: 'CLOSE_ABOVE_PREV_HIGH',
        context: 'Daily structure break  -  yesterday\'s sellers lost. Cleaner version of "Breakout" on daily frame.',
      },
      {
        id: 719,
        title: 'Close Below Prev Low',
        explanation: 'Today\'s close is below yesterday\'s low  -  sellers pushed past the previous floor. Clear sign of downward momentum.',
        rule: 'SELL  -  short on next open or bounce to yesterday\'s low (now resistance). SL above today\'s high.',
        visual: 'CLOSE_BELOW_PREV_LOW',
        context: 'Bearish mirror of card 718. When this fires on your stock = clear warning, check your SL.',
      },

      // BREAKOUT CATEGORY (5 cards)
      {
        id: 720,
        title: 'NR7  -  Narrow Range',
        explanation: 'Today\'s candle has the narrowest price range in 7 days  -  volatility has compressed. NR7 days often precede explosive moves.',
        rule: 'NEUTRAL  -  set alerts on both sides. BUY if tomorrow breaks above today\'s high. SELL if below today\'s low.',
        visual: 'size_matters',
        context: 'SETUP signal, not entry. Something big is about to happen  -  direction comes from tomorrow.',
      },
      {
        id: 721,
        title: 'Inside Day',
        explanation: 'Today\'s entire range is inside yesterday\'s range  -  the market is coiling. Breakout in either direction is coming soon.',
        rule: 'NEUTRAL  -  wait for breakout. BUY if price closes above the mother candle high. SELL if below the mother candle low.',
        visual: 'INSIDE_DAY',
        context: 'Similar to NR7 but more visual. Next candle that breaks mother candle\'s range = the signal.',
      },
      {
        id: 722,
        title: 'Breakout 20-Day High',
        explanation: 'Price closed above its 20-day high  -  a multi-week resistance has been cleared. Structural breakout.',
        rule: 'BUY on breakout day or tight pullback to breakout level. SL below the 20D high (now support).',
        visual: 'BREAKOUT_20D_HIGH',
        context: 'Quantitative "Breakout"  -  scanner checks 20 days, fires when ceiling breaks. Automated concept.',
      },
      {
        id: 723,
        title: 'Breakdown 20-Day Low',
        explanation: 'Price closed below its 20-day low  -  multi-week support has been broken. Bearish structural shift.',
        rule: 'SELL  -  enter on breakdown or dead-cat bounce back to broken level. SL above the 20D low.',
        visual: 'BREAKDOWN_20D_LOW',
        context: 'Bearish twin. Structural breakdown  -  not just a bad day. Review whether thesis still holds.',
      },
      {
        id: 724,
        title: 'Consolidation Breakout',
        explanation: 'Stock consolidated in a tight range (< 5% wide over 5-10 days) and just broke out  -  the coiled spring is releasing.',
        rule: 'BUY on breakout. Target = range width projected upward. SL below the bottom of the consolidation.',
        visual: 'CONSOLIDATION_BREAKOUT',
        context: '"Consolidation" concept resolves. NR7 + Inside Day for days, then boom  -  spring unwinds.',
      },

      // VOLUME CATEGORY (2 cards)
      {
        id: 725,
        title: 'Breakout + Volume Spike',
        explanation: 'Price broke above its 20-day high AND volume is 2x average  -  institutional confirmation. The highest-conviction breakout signal.',
        rule: 'BUY on breakout day  -  highest conviction entry. Volume confirms it\'s not a fake breakout. SL below the breakout level.',
        visual: 'BREAKOUT_VOLUME_SPIKE',
        context: 'Combines price breakout with volume proof. Highest-conviction setup on entire platform.',
      },
      {
        id: 726,
        title: 'Volume Spike',
        explanation: 'Volume today is 2x the 20-day average  -  something significant happened (news, institution, block deal). Volume alone is not directional.',
        rule: 'NEUTRAL  -  check direction. Price up on big volume -> BUY. Price down on big volume -> SELL. Never trade volume alone.',
        visual: 'VOLUME_SPIKE',
        context: 'Volume validates direction  -  but it doesn\'t MAKE the direction. Always check price.',
      },

      // PATTERN CATEGORY (1 card)
      {
        id: 727,
        title: 'Bullish Engulfing',
        explanation: 'Yesterday was a red candle; today is a green candle that completely engulfs it  -  buyers decisively overpowered sellers. Best candlestick reversal signal.',
        rule: 'BUY  -  strongest at key support after a downtrend. Combine with RSI oversold or volume spike for high confidence. SL below the engulfing low.',
        visual: 'BULLISH_ENGULFING',
        context: 'Multi-signal confluence: RSI Oversold + Support Bounce + Bullish Engulfing = highest-confidence setup.',
      },
    ],
    checkpoints: [
      { after_card: 4, question: 'RSI is at 35 and dropping. What does this tell you?', options: ['Strong buying opportunity', 'The stock is about to crash', 'Momentum is weakening  -  avoid new longs, wait for further confirmation'], correct_index: 2 },
      { after_card: 12, question: 'SMA50 crosses above SMA200 on a stock. What just happened?', options: ['Golden Cross  -  major  bullish long-term signal', 'Death Cross  -  sell everything', 'The stock is overbought'], correct_index: 0 },
      { after_card: 22, question: 'NR7 fires on a stock. What should you do?', options: ['Buy immediately  -  compressed range means it will go up', 'It\'s a setup signal  -  watch for direction on the next candle, then decide', 'Sell  -  narrow range means the stock is dying'], correct_index: 1 },
    ],
  },
];
export default levels_11_12_13;
